import { useEffect, useState, useCallback } from "react";
import { adminShop, adminCoins, uploadImage, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Edit3, RefreshCw, Coins, Package, Truck, Search, X, TicketPercent, History, Power } from "lucide-react";

const inputCls = "w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#FFBF00]/50 focus:outline-none text-sm";
const btnGold = "px-4 py-2 rounded-lg bg-[#FFBF00] text-black font-semibold text-sm blocky-shadow-black hover-blocky inline-flex items-center gap-2";

const EMPTY = {
  name: "",
  category: "General",
  short_description: "",
  description: "",
  images: [],
  price_inr: 0,
  perks: [],
  stock: null,
  featured: false,
  active: true,
  disable_dc_coin: false,
  mc_command: "",
  inv_slot: 0
};

/* ============ Products ============ */
export function AdminStoreProducts() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => adminShop.products().then(setRows).catch(() => toast.error("Load failed (check admin token)")), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = {
      ...editing,
      price_inr: Number(editing.price_inr) || 0,
      inv_slot: Number(editing.inv_slot) || 0,
      stock: editing.stock === "" || editing.stock === null ? null : Number(editing.stock),
      images: typeof editing.images === "string" ? editing.images.split(",").map((s) => s.trim()).filter(Boolean) : editing.images,
      perks: typeof editing.perks === "string" ? editing.perks.split("\n").map((s) => s.trim()).filter(Boolean) : editing.perks,
    };
    try {
      if (editing.id) await adminShop.updateProduct(editing.id, payload);
      else await adminShop.createProduct(payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete product?")) return; await adminShop.deleteProduct(id); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-store-products">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-xl flex items-center gap-2"><Package size={18} /> Products</h3>
        <button className={btnGold} onClick={() => setEditing({ ...EMPTY })} data-testid="admin-product-new"><Plus size={16} /> New Product</button>
      </div>
      {editing && (
        <div className="glass rounded-2xl p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="admin-product-form">
          <label className="text-xs text-white/50 sm:col-span-2">Name<input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-product-name" /></label>
          <label className="text-xs text-white/50">Category<input className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></label>
          <label className="text-xs text-white/50">Price (₹)<input type="number" className={inputCls} value={editing.price_inr} onChange={(e) => setEditing({ ...editing, price_inr: e.target.value })} data-testid="admin-product-price" /></label>
          <label className="text-xs text-white/50 sm:col-span-2">
            Short Description
            <textarea
              className={inputCls}
              rows={3}
              value={editing.short_description || ""}
              onChange={(e) => setEditing({ ...editing, short_description: e.target.value })}
              placeholder="Short product summary..."
              data-testid="admin-product-short-description"
            />
          </label>

          <label className="text-xs text-white/50 sm:col-span-2">
            Full Description
            <textarea
              className={inputCls}
              rows={8}
              value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              placeholder="Full product description / HTML..."
              data-testid="admin-product-description"
            />
          </label>
          <label className="text-xs text-white/50 sm:col-span-2">Image URLs (comma separated)<input className={inputCls} value={Array.isArray(editing.images) ? editing.images.join(", ") : editing.images} onChange={(e) => setEditing({ ...editing, images: e.target.value })} /></label>
          <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2 rounded-lg glass text-sm cursor-pointer hover-blocky inline-flex items-center gap-2" data-testid="admin-product-upload-label">
              <Plus size={14} /> Upload image
              <input type="file" accept="image/*" className="hidden" data-testid="admin-product-upload"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  try {
                    const res = await uploadImage(f);
                    const arr = Array.isArray(editing.images) ? editing.images : (editing.images ? editing.images.split(",").map((s) => s.trim()).filter(Boolean) : []);
                    setEditing({ ...editing, images: [...arr, res.url] });
                    toast.success("Image uploaded");
                  } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
                  e.target.value = "";
                }} />
            </label>
            <div className="flex gap-2">
              {(Array.isArray(editing.images) ? editing.images : (editing.images || "").split(",").map((s) => s.trim()).filter(Boolean)).slice(0, 5).map((src, i) => (
                <img key={i} src={fileUrl(src)} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
              ))}
            </div>
          </div>
          <label className="text-xs text-white/50 sm:col-span-2">Perks (one per line)<textarea className={inputCls} rows={3} value={Array.isArray(editing.perks) ? editing.perks.join("\n") : editing.perks} onChange={(e) => setEditing({ ...editing, perks: e.target.value })} /></label>
          <label className="text-xs text-white/50 sm:col-span-2">Minecraft Command — use <code className="text-[#FFBF00]">(**)</code> for username<input className={inputCls} value={editing.mc_command} onChange={(e) => setEditing({ ...editing, mc_command: e.target.value })} data-testid="admin-product-command" placeholder="lp user (**) parent set knight" /></label>
          <label className="text-xs text-white/50">Inventory Slot<input type="number" className={inputCls} value={editing.inv_slot} onChange={(e) => setEditing({ ...editing, inv_slot: e.target.value })} /></label>
          <label className="text-xs text-white/50">Stock (blank = unlimited)<input type="number" className={inputCls} value={editing.stock ?? ""} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} /></label>
          <div className="flex gap-6 items-center sm:col-span-2 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.disable_dc_coin} onChange={(e) => setEditing({ ...editing, disable_dc_coin: e.target.checked })} /> Disable DC Coin</label>
          </div>
          <div className="flex gap-3 sm:col-span-2">
            <button className={btnGold} onClick={save} data-testid="admin-product-save"><Save size={16} /> Save</button>
            <button className="px-4 py-2 rounded-lg glass text-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className="glass rounded-xl p-4" data-testid={`admin-product-${p.slug}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display">{p.name}</div>
                <div className="text-xs text-white/50">{p.category} · ₹{p.price_inr}</div>
              </div>
              <div className="flex gap-2">
                <button
  onClick={() => setEditing({ ...p })}
  className="text-white/60 hover:text-[#FFBF00] inline-flex items-center gap-1.5"
  title="Edit product"
  aria-label={`Edit ${p.name}`}
>
  <Edit3 size={15} />
  <span className="hidden sm:inline">Edit</span>
</button>
                <button onClick={() => del(p.id)} className="text-white/60 hover:text-red-400" data-testid={`admin-product-del-${p.slug}`}><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="flex gap-2 mt-2 flex-wrap text-[10px]">
              {p.featured && <span className="px-2 py-0.5 rounded-full bg-[#FFBF00]/20 text-[#FFBF00]">Featured</span>}
              {!p.active && <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">Inactive</span>}
              {p.disable_dc_coin && <span className="px-2 py-0.5 rounded-full bg-white/10">No DC</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ Orders ============ */
export function AdminStoreOrders() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => adminShop.orders(q).then(setRows).catch(() => {}), [q]);
  useEffect(() => { load(); }, [load]);
  const complete = async (n) => { await adminShop.completeOrder(n); toast.success("Marked paid + delivered"); load(); };
  const cancel = async (n) => { await adminShop.cancelOrder(n); toast.success("Cancelled"); load(); };

  return (
    <div data-testid="admin-store-orders">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <h3 className="font-display text-xl flex items-center gap-2"><Package size={18} /> Orders</h3>
        <div className="flex gap-2">
          <input className={inputCls + " w-56"} placeholder="Search order / LostDC ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-orders-search" />
          <button className="px-3 py-2 rounded-lg glass" onClick={load}><RefreshCw size={15} /></button>
        </div>
      </div>
      <div className="overflow-x-auto glass rounded-2xl p-2">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-white/40 text-xs uppercase"><th className="text-left p-2">Order</th><th className="text-left p-2">LostDC ID</th><th className="text-left p-2">Items</th><th className="text-right p-2">Total</th><th className="text-left p-2">Method</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.order_number} className="border-t border-white/5" data-testid={`admin-order-${o.order_number}`}>
                <td className="p-2 font-mono text-[#FFBF00]">{o.order_number}</td>
                <td className="p-2">{o.lostdc_id}</td>
                <td className="p-2 text-white/60">{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</td>
                <td className="p-2 text-right">₹{o.total_inr.toFixed(2)}{o.dc_used > 0 && <span className="text-[#FFBF00]"> +{o.dc_used}DC</span>}</td>
                <td className="p-2">{o.payment_method}</td>
                <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-xs ${o.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : o.status === "cancelled" ? "bg-red-500/15 text-red-300" : "bg-yellow-500/15 text-yellow-300"}`}>{o.status}</span></td>
                <td className="p-2 text-right whitespace-nowrap">
                  {o.status !== "paid" && <button onClick={() => complete(o.order_number)} className="text-emerald-400 mr-3 text-xs" data-testid={`admin-order-complete-${o.order_number}`}>Complete</button>}
                  {o.status === "pending" && <button onClick={() => cancel(o.order_number)} className="text-red-400 text-xs">Cancel</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-white/40">No orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Deliveries ============ */
export function AdminStoreDeliveries() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const load = useCallback(() => adminShop.storeOrders(search).then(setRows).catch(() => {}), [search]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await adminShop.setStoreOrderStatus(id, status); load(); };

  return (
    <div data-testid="admin-store-deliveries">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <h3 className="font-display text-xl flex items-center gap-2"><Truck size={18} /> Minecraft Deliveries</h3>
        <input className={inputCls + " w-64"} placeholder="Search order / username / product" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-deliveries-search" />
      </div>
      <div className="overflow-x-auto glass rounded-2xl p-2">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-white/40 text-xs uppercase"><th className="text-left p-2">Order</th><th className="text-left p-2">Username</th><th className="text-left p-2">Product</th><th className="text-left p-2">Command</th><th className="text-left p-2">Status</th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-white/5" data-testid={`admin-delivery-${d.id}`}>
                <td className="p-2 font-mono text-[#FFBF00]">{d.order_number}</td>
                <td className="p-2">{d.minecraft_username}</td>
                <td className="p-2">{d.product_name}</td>
                <td className="p-2"><code className="text-[#FFBF00] text-xs">{d.command}</code></td>
                <td className="p-2">
                  <select value={d.status} onChange={(e) => setStatus(d.id, e.target.value)} className="bg-black/50 border border-white/10 rounded px-2 py-1 text-xs">
                    {["pending", "claimed", "delivered"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-white/40">No deliveries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Coin Manager ============ */
export function AdminCoinManager() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [coinValue, setCoinValue] = useState(2);
  const [edits, setEdits] = useState({});
  const load = useCallback(() => adminCoins.users(q).then(setRows).catch(() => toast.error("Load failed (check admin token)")), [q]);
  useEffect(() => { load(); adminCoins.getSettings().then((s) => setCoinValue(s.dc_coin_value)).catch(() => {}); }, [load]);

  const saveValue = async () => { await adminCoins.setSettings(Number(coinValue) || 2); toast.success("Coin value updated"); };
  const saveBalance = async (uid) => {
    const v = edits[uid]; if (v === undefined) return;
    const r = await adminCoins.setBalance(uid, Number(v) || 0); toast.success("Balance updated");
    setRows((prev) => prev.map((u) => u.user_id === uid ? { ...u, dc_coin: r.dc_coin } : u));
    setEdits((p) => { const n = { ...p }; delete n[uid]; return n; });
  };

  return (
    <div data-testid="admin-coin-manager">
      <div className="glass rounded-2xl p-5 mb-6 flex items-end gap-3 flex-wrap">
        <label className="text-xs text-white/50">DC Coins per ₹1<input type="number" className={inputCls + " w-32"} value={coinValue} onChange={(e) => setCoinValue(e.target.value)} data-testid="admin-coin-value" /></label>
        <button className={btnGold} onClick={saveValue} data-testid="admin-coin-value-save"><Save size={15} /> Save value</button>
        <div className="ml-auto flex gap-2">
          <input className={inputCls + " w-56"} placeholder="Search user" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-coin-search" />
          <button className="px-3 py-2 rounded-lg glass" onClick={load}><Search size={15} /></button>
        </div>
      </div>
      <div className="overflow-x-auto glass rounded-2xl p-2">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-white/40 text-xs uppercase"><th className="text-left p-2">LostDC ID</th><th className="text-left p-2">Name</th><th className="text-left p-2">Balance</th><th className="text-right p-2">Set</th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5" data-testid={`admin-coin-user-${u.user_id}`}>
                <td className="p-2 text-[#FFBF00]">{u.lostdc_id || "—"}</td>
                <td className="p-2 text-white/70">{u.name}</td>
                <td className="p-2 font-semibold">{u.dc_coin} DC</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <input type="number" className="w-24 px-2 py-1 rounded bg-black/50 border border-white/10 text-sm mr-2"
                    value={edits[u.user_id] ?? u.dc_coin} onChange={(e) => setEdits({ ...edits, [u.user_id]: e.target.value })} data-testid={`admin-coin-input-${u.user_id}`} />
                  <button onClick={() => saveBalance(u.user_id)} className="text-emerald-400 text-xs" data-testid={`admin-coin-save-${u.user_id}`}>Save</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-white/40">No users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ Server Sync ============ */
export function AdminServerSync() {
  const [accounts, setAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [pending, setPending] = useState([]);
  const [form, setForm] = useState({ lostdc_id: "", balance: 0, server: "" });
  const load = useCallback(() => {
    adminCoins.syncAccounts().then(setAccounts).catch(() => {});
    adminCoins.syncLogs().then(setLogs).catch(() => {});
    adminCoins.syncPending().then(setPending).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addAcc = async () => {
    if (!form.lostdc_id) return;
    await adminCoins.addSyncAccount({ ...form, balance: Number(form.balance) || 0 });
    setForm({ lostdc_id: "", balance: 0, server: "" }); toast.success("Account saved"); load();
  };
  const delAcc = async (id) => { await adminCoins.delSyncAccount(id); load(); };
  const runSync = async () => { const r = await adminCoins.runSync(); toast.success(`Synced ${r.synced} users (+${r.credited} DC), ${r.pending} pending`); load(); };

  return (
    <div data-testid="admin-server-sync">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h3 className="font-display text-xl flex items-center gap-2"><RefreshCw size={18} /> Enterprise Server Sync</h3>
        <button className={btnGold} onClick={runSync} data-testid="admin-sync-run"><RefreshCw size={16} /> Run Manual Sync</button>
      </div>
      <div className="glass rounded-2xl p-5 mb-6 grid sm:grid-cols-4 gap-3 items-end">
        <label className="text-xs text-white/50">LostDC ID<input className={inputCls} value={form.lostdc_id} onChange={(e) => setForm({ ...form, lostdc_id: e.target.value })} data-testid="sync-acc-id" /></label>
        <label className="text-xs text-white/50">Store Balance<input type="number" className={inputCls} value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="sync-acc-balance" /></label>
        <label className="text-xs text-white/50">Server<input className={inputCls} value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} /></label>
        <button className={btnGold} onClick={addAcc} data-testid="sync-acc-add"><Plus size={16} /> Add/Update</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-4">
          <div className="text-sm uppercase tracking-widest text-white/40 mb-3">Store Accounts</div>
          {accounts.map((a) => (
            <div key={a.lostdc_id} className="flex justify-between items-center py-2 border-b border-white/5 text-sm">
              <span>{a.lostdc_id} <span className="text-white/40">({a.server || "—"})</span></span>
              <span className="flex items-center gap-3"><b className="text-[#FFBF00]">{a.balance}</b><button onClick={() => delAcc(a.lostdc_id)} className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button></span>
            </div>
          ))}
          {accounts.length === 0 && <div className="text-white/40 text-sm py-4">No store accounts. Add one or POST to /api/coins/sync/ingest.</div>}
        </div>
        <div className="glass rounded-2xl p-4">
          <div className="text-sm uppercase tracking-widest text-white/40 mb-3">Pending (no wallet match)</div>
          {pending.map((p) => (
            <div key={p.lostdc_id} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span>{p.lostdc_id} <span className="text-white/40">({p.server_name})</span></span>
              <b className="text-yellow-300">{p.pending_balance}</b>
            </div>
          ))}
          {pending.length === 0 && <div className="text-white/40 text-sm py-4">No pending users.</div>}
        </div>
      </div>

      <div className="glass rounded-2xl p-4 mt-6">
        <div className="text-sm uppercase tracking-widest text-white/40 mb-3">Recent Sync Logs</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-white/40 text-xs uppercase"><th className="text-left p-2">User</th><th className="text-left p-2">Amount</th><th className="text-left p-2">Server</th><th className="text-left p-2">Status</th><th className="text-left p-2">Date</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-white/5">
                  <td className="p-2">{l.lostdc_id}</td><td className="p-2 text-[#FFBF00]">+{l.synced_amount}</td>
                  <td className="p-2">{l.server_name}</td><td className="p-2 text-emerald-300">{l.status}</td>
                  <td className="p-2 text-white/40">{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40">No logs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ============ Discord Coupons ============ */

const EMPTY_COUPON = {
  code: "",
  discount_type: "percentage",
  discount_value: 10,
  min_cart_value: 0,
  max_discount: "",
  starts_at: "",
  expires_at: "",
  usage_limit: "",
  per_user_limit: 1,
  allowed_users: "",
  allowed_products: [],
  active: true,
};

const couponDateForInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const couponDateForApi = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export function AdminStoreCoupons() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [coupons, productRows] = await Promise.all([
        adminShop.coupons(),
        adminShop.products(),
      ]);
      setRows(coupons || []);
      setProducts(productRows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coupons");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setUsage(null);
    setEditing({ ...EMPTY_COUPON });
  };

  const startEdit = (c) => {
    setUsage(null);
    setEditing({
      ...c,
      max_discount: c.max_discount ?? "",
      starts_at: couponDateForInput(c.starts_at),
      expires_at: couponDateForInput(c.expires_at),
      usage_limit: c.usage_limit ?? "",
      per_user_limit: c.per_user_limit ?? 1,
      allowed_users: (c.allowed_users || []).join(", "),
      allowed_products: c.allowed_products || [],
      min_cart_value: c.min_cart_value ?? 0,
      discount_value: c.discount_value ?? 0,
    });
  };

  const save = async () => {
    if (!editing?.code?.trim()) {
      toast.error("Coupon code is required");
      return;
    }

    const payload = {
      code: editing.code.trim().toUpperCase(),
      discount_type: editing.discount_type,
      discount_value: Number(editing.discount_value) || 0,
      min_cart_value: Number(editing.min_cart_value) || 0,
      max_discount:
        editing.max_discount === "" || editing.max_discount == null
          ? null
          : Number(editing.max_discount),
      starts_at: couponDateForApi(editing.starts_at),
      expires_at: couponDateForApi(editing.expires_at),
      usage_limit:
        editing.usage_limit === "" || editing.usage_limit == null
          ? null
          : Number(editing.usage_limit),
      per_user_limit: Math.max(1, Number(editing.per_user_limit) || 1),
      allowed_users: String(editing.allowed_users || "")
        .split(",")
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean),
      allowed_products: editing.allowed_products || [],
      active: !!editing.active,
    };

    try {
      setBusy(true);

      if (editing.id) {
        await adminShop.updateCoupon(editing.id, payload);
      } else {
        await adminShop.createCoupon(payload);
      }

      toast.success(editing.id ? "Coupon updated" : "Coupon created");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save coupon");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await adminShop.updateCoupon(coupon.id, {
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_cart_value: coupon.min_cart_value || 0,
        max_discount: coupon.max_discount ?? null,
        starts_at: coupon.starts_at || null,
        expires_at: coupon.expires_at || null,
        usage_limit: coupon.usage_limit ?? null,
        per_user_limit: coupon.per_user_limit || 1,
        allowed_users: coupon.allowed_users || [],
        allowed_products: coupon.allowed_products || [],
        active: !coupon.active,
      });

      toast.success(coupon.active ? "Coupon disabled" : "Coupon enabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update coupon");
    }
  };

  const del = async (id) => {
    if (
      !window.confirm(
        "Delete this coupon? Existing paid orders will keep their recorded discount."
      )
    ) return;

    try {
      await adminShop.deleteCoupon(id);
      toast.success("Coupon deleted");

      if (usage?.couponId === id) {
        setUsage(null);
      }

      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const showUsage = async (coupon) => {
    try {
      const rows = await adminShop.couponUsage(coupon.id);
      setUsage({
        couponId: coupon.id,
        code: coupon.code,
        rows: rows || [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load usage history");
    }
  };

  return (
    <div data-testid="admin-store-coupons">

      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div>
          <h3 className="font-display text-xl flex items-center gap-2">
            <TicketPercent size={19} />
            Discord Coupons
          </h3>

          <p className="text-xs text-white/40 mt-1">
            Create discount codes to distribute through Discord.
          </p>
        </div>

        <button
          className={btnGold}
          onClick={startNew}
          data-testid="admin-coupon-new"
        >
          <Plus size={16} />
          New Coupon
        </button>
      </div>

      {editing && (
        <div
          className="glass rounded-2xl p-5 mb-6"
          data-testid="admin-coupon-form"
        >
          <div className="flex justify-between items-center mb-4">
            <div className="font-semibold text-lg">
              {editing.id ? "Edit Coupon" : "Create Coupon"}
            </div>

            <button
              className="text-white/50 hover:text-white"
              onClick={() => setEditing(null)}
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">

            <label className="text-xs text-white/50">
              Coupon Code
              <input
                className={inputCls + " uppercase"}
                value={editing.code}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="DISCORD10"
              />
            </label>

            <label className="text-xs text-white/50">
              Discount Type
              <select
                className={inputCls}
                value={editing.discount_type}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    discount_type: e.target.value,
                  })
                }
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </label>

            <label className="text-xs text-white/50">
              Discount Value
              <input
                type="number"
                min="0"
                className={inputCls}
                value={editing.discount_value}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    discount_value: e.target.value,
                  })
                }
              />
            </label>

            <label className="text-xs text-white/50">
              Minimum Cart (₹)
              <input
                type="number"
                min="0"
                className={inputCls}
                value={editing.min_cart_value}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    min_cart_value: e.target.value,
                  })
                }
              />
            </label>

            <label className="text-xs text-white/50">
              Maximum Discount (₹)
              <input
                type="number"
                min="0"
                className={inputCls}
                value={editing.max_discount}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    max_discount: e.target.value,
                  })
                }
                placeholder="No limit"
              />
            </label>

            <label className="text-xs text-white/50">
              Global Usage Limit
              <input
                type="number"
                min="1"
                className={inputCls}
                value={editing.usage_limit}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    usage_limit: e.target.value,
                  })
                }
                placeholder="Unlimited"
              />
            </label>

            <label className="text-xs text-white/50">
              Per User Limit
              <input
                type="number"
                min="1"
                className={inputCls}
                value={editing.per_user_limit}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    per_user_limit: e.target.value,
                  })
                }
              />
            </label>

            <label className="text-xs text-white/50">
              Starts At
              <input
                type="datetime-local"
                className={inputCls}
                value={editing.starts_at || ""}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    starts_at: e.target.value,
                  })
                }
              />
            </label>

            <label className="text-xs text-white/50">
              Expires At
              <input
                type="datetime-local"
                className={inputCls}
                value={editing.expires_at || ""}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    expires_at: e.target.value,
                  })
                }
              />
            </label>

            <label className="text-xs text-white/50 sm:col-span-2 lg:col-span-3">
              Allowed Users
              <input
                className={inputCls}
                value={editing.allowed_users}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    allowed_users: e.target.value,
                  })
                }
                placeholder="lostdc_id, email, or user id — comma separated. Leave empty for everyone."
              />
            </label>
          </div>

          <div className="mt-4">
            <div className="text-xs text-white/50 mb-2">
              Allowed Products
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-52 overflow-y-auto rounded-xl border border-white/10 p-3 bg-black/20">

              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer sm:col-span-2 lg:col-span-3 pb-2 border-b border-white/5">
                <input
                  type="checkbox"
                  checked={!editing.allowed_products?.length}
                  onChange={() =>
                    setEditing({
                      ...editing,
                      allowed_products: [],
                    })
                  }
                />
                <span>All products</span>
              </label>

              {products.map((product) => {
                const checked = (
                  editing.allowed_products || []
                ).includes(product.id);

                return (
                  <label
                    key={product.id}
                    className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setEditing({
                          ...editing,
                          allowed_products: checked
                            ? editing.allowed_products.filter(
                                (id) => id !== product.id
                              )
                            : [
                                ...(editing.allowed_products || []),
                                product.id,
                              ],
                        })
                      }
                    />

                    <span className="truncate">
                      {product.name}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="text-[11px] text-white/35 mt-2">
              Leave all products selected for a global coupon.
            </div>
          </div>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!editing.active}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    active: e.target.checked,
                  })
                }
              />
              Active
            </label>

            <div className="flex gap-2">
              <button
                className="px-4 py-2 rounded-lg border border-white/10 text-sm"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>

              <button
                className={btnGold}
                onClick={save}
                disabled={busy}
              >
                <Save size={15} />
                {busy ? "Saving..." : "Save Coupon"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-white/40">
          No coupons created yet.
        </div>
      ) : (
        <div className="space-y-3">

          {rows.map((coupon) => (
            <div
              key={coupon.id}
              className="glass rounded-2xl p-4"
              data-testid={`admin-coupon-${coupon.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">

                <div>
                  <div className="flex items-center gap-2 flex-wrap">

                    <code className="px-2 py-1 rounded bg-black/50 text-[#FFBF00] font-bold tracking-wider">
                      {coupon.code}
                    </code>

                    <span
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
                        coupon.active
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-red-500/15 text-red-300"
                      }`}
                    >
                      {coupon.active ? "Active" : "Disabled"}
                    </span>
                  </div>

                  <div className="text-sm mt-2">
                    {coupon.discount_type === "percentage"
                      ? `${coupon.discount_value}% OFF`
                      : `₹${Number(coupon.discount_value || 0).toFixed(2)} OFF`}

                    {coupon.max_discount != null &&
                      ` · max ₹${Number(coupon.max_discount).toFixed(2)}`}

                    {Number(coupon.min_cart_value || 0) > 0 &&
                      ` · min ₹${Number(coupon.min_cart_value).toFixed(2)}`}
                  </div>

                  <div className="text-xs text-white/40 mt-1">
                    Used {coupon.usage_count || 0}
                    {coupon.usage_limit != null
                      ? ` / ${coupon.usage_limit}`
                      : ""}

                    {" · "}
                    {coupon.per_user_limit || 1} per user

                    {coupon.allowed_products?.length
                      ? ` · ${coupon.allowed_products.length} product${
                          coupon.allowed_products.length === 1 ? "" : "s"
                        }`
                      : " · all products"}

                    {coupon.allowed_users?.length
                      ? ` · ${coupon.allowed_users.length} restricted user${
                          coupon.allowed_users.length === 1 ? "" : "s"
                        }`
                      : " · all users"}
                  </div>

                  {(coupon.starts_at || coupon.expires_at) && (
                    <div className="text-[11px] text-white/30 mt-1">
                      {coupon.starts_at
                        ? `From ${new Date(coupon.starts_at).toLocaleString()}`
                        : "Immediately"}

                      {coupon.expires_at
                        ? ` · Until ${new Date(coupon.expires_at).toLocaleString()}`
                        : " · No expiry"}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">

                  <button
                    className="px-3 py-2 rounded-lg border border-white/10 text-xs inline-flex items-center gap-1"
                    onClick={() => toggleActive(coupon)}
                  >
                    <Power size={13} />
                    {coupon.active ? "Disable" : "Enable"}
                  </button>

                  <button
                    className="px-3 py-2 rounded-lg border border-white/10 text-xs inline-flex items-center gap-1"
                    onClick={() => showUsage(coupon)}
                  >
                    <History size={13} />
                    Usage
                  </button>

                  <button
                    className="px-3 py-2 rounded-lg border border-white/10 text-xs inline-flex items-center gap-1"
                    onClick={() => startEdit(coupon)}
                  >
                    <Edit3 size={13} />
                    Edit
                  </button>

                  <button
                    className="px-3 py-2 rounded-lg border border-red-500/20 text-red-300 text-xs inline-flex items-center gap-1"
                    onClick={() => del(coupon.id)}
                  >
                    <Trash2 size={13} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {usage && (
        <div
          className="glass rounded-2xl p-5 mt-6"
          data-testid="admin-coupon-usage"
        >
          <div className="flex justify-between items-center mb-4">

            <div>
              <div className="font-semibold">
                Usage History — {usage.code}
              </div>

              <div className="text-xs text-white/40">
                {usage.rows.length} recorded use
                {usage.rows.length === 1 ? "" : "s"}
              </div>
            </div>

            <button
              className="text-white/50 hover:text-white"
              onClick={() => setUsage(null)}
            >
              <X size={18} />
            </button>
          </div>

          <div className="overflow-x-auto">

            <table className="w-full text-xs min-w-[650px]">

              <thead>
                <tr className="text-white/40 uppercase tracking-widest border-b border-white/10">
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Order</th>
                  <th className="text-left p-2">Discount</th>
                  <th className="text-left p-2">Used At</th>
                </tr>
              </thead>

              <tbody>
                {usage.rows.map((u) => (
                  <tr
                    key={u.id || u.order_number}
                    className="border-b border-white/5"
                  >
                    <td className="p-2">
                      {u.lostdc_id || u.user_id || "—"}
                    </td>

                    <td className="p-2 text-[#FFBF00]">
                      {u.order_number || "—"}
                    </td>

                    <td className="p-2">
                      ₹{Number(u.discount_inr || 0).toFixed(2)}
                    </td>

                    <td className="p-2 text-white/40">
                      {u.used_at
                        ? new Date(u.used_at).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}

                {usage.rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="p-6 text-center text-white/40"
                    >
                      No usage recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
